import { useEffect, useState } from 'react';

import {
  TEMPERATURE_UNIT_CHANGED_EVENT,
  TEMPERATURE_UNIT_STORAGE_KEY,
} from '@/shared/hooks/use-temperature-unit/use-temperature-unit.constants';
import { TTemperatureUnit } from '@/shared/hooks/use-temperature-unit/use-temperature-unit.types';

const readStoredUnit = (): TTemperatureUnit =>
  localStorage.getItem(TEMPERATURE_UNIT_STORAGE_KEY) === 'F' ? 'F' : 'C';

/**
 * Remembers the °C/°F choice as a user preference in localStorage, staying in
 * sync across hook instances and browser tabs.
 */
export const useTemperatureUnit = () => {
  const [temperatureUnit, setUnit] = useState<TTemperatureUnit>(readStoredUnit);

  useEffect(() => {
    const sync = () => setUnit(readStoredUnit());

    window.addEventListener(TEMPERATURE_UNIT_CHANGED_EVENT, sync);
    const handleStorage = (event: StorageEvent) => {
      if (event.key === TEMPERATURE_UNIT_STORAGE_KEY) {
        sync();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(TEMPERATURE_UNIT_CHANGED_EVENT, sync);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setTemperatureUnit = (unit: TTemperatureUnit) => {
    localStorage.setItem(TEMPERATURE_UNIT_STORAGE_KEY, unit);
    setUnit(unit);
    window.dispatchEvent(new CustomEvent(TEMPERATURE_UNIT_CHANGED_EVENT));
  };

  return { temperatureUnit, setTemperatureUnit };
};
